import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import { environment } from '../../environments/environment';

const AUTH_KEY = 'ims-auth';
const OTP_KEY = 'ims-otp-pending';
const RESET_KEY = 'ims-password-reset-pending';

export interface AuthResult {
  success: boolean;
  message?: string;
}

interface AuthSession {
  email: string;
  pin: string;
  userId: number;
  fullName: string;
  token: string;
  branchId?: number;
  authenticatedAt: string;
}

interface OtpSession {
  email: string;
  pin: string;
  userId: number;
  expiresAt: string;
  peekedOtp?: string;
}

interface ResetSession {
  email: string;
}

interface OtpSentResponse {
  userID?: number;
  userId?: number;
  message?: string;
}

interface VerifyOtpResponse {
  success?: boolean;
  Success?: boolean;
  token?: string;
  Token?: string;
  message?: string;
  Message?: string;
  fullName?: string;
  FullName?: string;
  surname?: string;
  Surname?: string;
  emailAddress?: string;
  EmailAddress?: string;
  userID?: number;
  userId?: number;
  branchID?: number;
  branchId?: number;
  BranchID?: number;
  BranchId?: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authenticated = signal(false);
  private readonly userEmail = signal('');
  private readonly peekedOtp = signal('');

  readonly isAuthenticated = this.authenticated.asReadonly();
  readonly currentUserEmail = this.userEmail.asReadonly();

  /** Shown on OTP page when a dev peek succeeds (or mock OTP). */
  get demoOtp(): string {
    return this.peekedOtp() || this.getOtpSession()?.peekedOtp || '';
  }

  constructor() {
    this.restoreSession();
  }

  beginLogin(email: string, password: string): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      return of({
        success: false,
        message: 'Please enter both email and password.',
      });
    }

    if (!this.isValidEmail(normalizedEmail)) {
      return of({
        success: false,
        message: 'Please enter a valid email address.',
      });
    }

    sessionStorage.removeItem(AUTH_KEY);
    this.authenticated.set(false);
    this.userEmail.set('');
    this.peekedOtp.set('');

    return this.http
      .post<OtpSentResponse>(`${environment.apiUrl}/Auth/login`, {
        emailAddress: normalizedEmail,
        password,
      })
      .pipe(
        switchMap((response) => {
          const userId = response.userID ?? response.userId;
          if (!userId) {
            return of({
              success: false,
              message: 'Login succeeded but no user id was returned.',
            } satisfies AuthResult);
          }

          const otpSession: OtpSession = {
            email: normalizedEmail,
            pin: password,
            userId,
            expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
          };

          if (!environment.peekDevOtp) {
            sessionStorage.setItem(OTP_KEY, JSON.stringify(otpSession));
            return of({ success: true } satisfies AuthResult);
          }

          return this.http
            .get<{
              otp?: string;
              Otp?: string;
            }>(`${environment.apiUrl}/Auth/dev/peek-login-otp/${userId}`)
            .pipe(
              map((peek) => {
                const code = peek.otp ?? peek.Otp ?? '';
                otpSession.peekedOtp = code;
                this.peekedOtp.set(code);
                sessionStorage.setItem(OTP_KEY, JSON.stringify(otpSession));
                return { success: true } satisfies AuthResult;
              }),
              catchError(() => {
                sessionStorage.setItem(OTP_KEY, JSON.stringify(otpSession));
                return of({ success: true } satisfies AuthResult);
              }),
            );
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to sign in.')),
        ),
      );
  }

  verifyOtp(code: string): Observable<AuthResult> {
    const session = this.getOtpSession();

    if (!session) {
      return of({
        success: false,
        message: 'Your verification session expired. Please sign in again.',
      });
    }

    if (new Date(session.expiresAt).getTime() < Date.now()) {
      sessionStorage.removeItem(OTP_KEY);
      return of({
        success: false,
        message: 'Your verification code has expired. Please sign in again.',
      });
    }

    return this.http
      .post<VerifyOtpResponse>(`${environment.apiUrl}/Auth/verify-otp`, {
        userID: session.userId,
        otp: code.trim(),
      })
      .pipe(
        map((response) => {
          const success = response.success ?? response.Success ?? false;
          const token = response.token ?? response.Token ?? '';
          if (!success || !token) {
            return {
              success: false,
              message:
                response.message ??
                response.Message ??
                'Invalid verification code.',
            } satisfies AuthResult;
          }

          const fullName =
            [
              response.fullName ?? response.FullName,
              response.surname ?? response.Surname,
            ]
              .filter((part) => !!part && String(part).trim().length > 0)
              .join(' ')
              .trim() || session.email;

          const branchId =
            response.branchID ??
            response.branchId ??
            response.BranchID ??
            response.BranchId ??
            0;

          const authSession: AuthSession = {
            email: session.email,
            pin: session.pin,
            userId: session.userId,
            fullName,
            token,
            branchId:
              Number.isFinite(Number(branchId)) && Number(branchId) > 0
                ? Number(branchId)
                : undefined,
            authenticatedAt: new Date().toISOString(),
          };

          sessionStorage.setItem(AUTH_KEY, JSON.stringify(authSession));
          sessionStorage.removeItem(OTP_KEY);
          this.authenticated.set(true);
          this.userEmail.set(session.email);
          this.peekedOtp.set('');
          return { success: true } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(
            this.toAuthError(
              error,
              'Invalid verification code. Please try again.',
            ),
          ),
        ),
      );
  }

  requestPasswordReset(email: string): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return of({
        success: false,
        message: 'Please enter your email address.',
      });
    }

    if (!this.isValidEmail(normalizedEmail)) {
      return of({
        success: false,
        message: 'Please enter a valid email address.',
      });
    }

    return this.http
      .post<OtpSentResponse>(`${environment.apiUrl}/Auth/forgot-password`, {
        emailAddress: normalizedEmail,
      })
      .pipe(
        switchMap(() => {
          sessionStorage.setItem(
            RESET_KEY,
            JSON.stringify({ email: normalizedEmail } satisfies ResetSession),
          );

          if (!environment.peekDevOtp) {
            return of({ success: true } satisfies AuthResult);
          }

          return this.http
            .get<{
              otp?: string;
              Otp?: string;
            }>(
              `${environment.apiUrl}/Auth/dev/peek-reset-otp/${encodeURIComponent(normalizedEmail)}`,
            )
            .pipe(
              map((peek) => {
                const code = peek.otp ?? peek.Otp ?? '';
                this.peekedOtp.set(code);
                return {
                  success: true,
                  message: code ? `Reset OTP (dev): ${code}` : undefined,
                } satisfies AuthResult;
              }),
              catchError(() => of({ success: true } satisfies AuthResult)),
            );
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to start password reset.')),
        ),
      );
  }

  resetPassword(
    email: string,
    newPassword: string,
    resetOtp: string,
  ): Observable<AuthResult> {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !newPassword || !resetOtp) {
      return of({
        success: false,
        message: 'Email, new password, and reset OTP are required.',
      });
    }

    return this.http
      .post(
        `${environment.apiUrl}/Auth/reset-password`,
        {
          emailAddress: normalizedEmail,
          newPassword,
          resetOtp: resetOtp.trim(),
        },
        { responseType: 'text' },
      )
      .pipe(
        map(() => {
          this.clearPendingReset();
          this.peekedOtp.set('');
          return { success: true } satisfies AuthResult;
        }),
        catchError((error: unknown) =>
          of(this.toAuthError(error, 'Unable to reset password.')),
        ),
      );
  }

  canResetPassword(email: string): boolean {
    const session = this.getResetSession();
    if (!session) {
      return false;
    }

    return session.email === email.trim().toLowerCase();
  }

  clearPendingReset(): void {
    sessionStorage.removeItem(RESET_KEY);
  }

  hasPendingOtp(): boolean {
    const session = this.getOtpSession();
    if (!session) {
      return false;
    }

    if (new Date(session.expiresAt).getTime() < Date.now()) {
      sessionStorage.removeItem(OTP_KEY);
      return false;
    }

    return true;
  }

  getPendingEmail(): string {
    return this.getOtpSession()?.email ?? '';
  }

  getMaskedEmail(): string {
    const email = this.getPendingEmail();
    if (!email) {
      return '';
    }

    const [local, domain] = email.split('@');
    if (!local || !domain) {
      return email;
    }

    if (local.length <= 2) {
      return `${local[0] ?? ''}***@${domain}`;
    }

    return `${local[0]}${'*'.repeat(Math.max(local.length - 2, 3))}${local.at(-1)}@${domain}`;
  }

  resendOtp(): Observable<AuthResult> {
    const session = this.getOtpSession();
    if (!session) {
      return of({
        success: false,
        message: 'Unable to resend code. Please sign in again.',
      });
    }

    return this.beginLogin(session.email, session.pin);
  }

  logout(): void {
    sessionStorage.removeItem(AUTH_KEY);
    sessionStorage.removeItem(OTP_KEY);
    sessionStorage.removeItem(RESET_KEY);
    this.authenticated.set(false);
    this.userEmail.set('');
    this.peekedOtp.set('');
  }

  clearPendingOtp(): void {
    sessionStorage.removeItem(OTP_KEY);
    this.peekedOtp.set('');
  }

  getCredentials(): { email: string; pin: string } | null {
    const session = this.getAuthSession();
    if (!session?.email || !session.pin) {
      return null;
    }

    return { email: session.email, pin: session.pin };
  }

  /** Logged-in user's home branch (from UserCredentials.BranchID). */
  currentBranchId(): number {
    return this.getAuthSession()?.branchId ?? 0;
  }

  /** Persist branch after settings/branches API load. */
  setUserBranchId(branchId: number): void {
    if (!Number.isFinite(branchId) || branchId <= 0) {
      return;
    }

    const session = this.getAuthSession();
    if (!session) {
      return;
    }

    if (session.branchId === branchId) {
      return;
    }

    session.branchId = branchId;
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(session));
  }

  /** Logged-in user's database id (for CreatedByUserID / ReportedByUserID). */
  currentUserId(): number {
    return this.getAuthSession()?.userId ?? 0;
  }

  /** Display name from profile (FullName + Surname), falling back to email. */
  currentUserName(): string {
    const session = this.getAuthSession();
    return session?.fullName?.trim() || session?.email || '';
  }

  private restoreSession(): void {
    const session = this.getAuthSession();
    if (!session?.email || !session.token) {
      return;
    }

    this.authenticated.set(true);
    this.userEmail.set(session.email);
  }

  private getAuthSession(): AuthSession | null {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      sessionStorage.removeItem(AUTH_KEY);
      return null;
    }
  }

  private getOtpSession(): OtpSession | null {
    const raw = sessionStorage.getItem(OTP_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as OtpSession;
    } catch {
      sessionStorage.removeItem(OTP_KEY);
      return null;
    }
  }

  private getResetSession(): ResetSession | null {
    const raw = sessionStorage.getItem(RESET_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ResetSession;
    } catch {
      sessionStorage.removeItem(RESET_KEY);
      return null;
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private toAuthError(error: unknown, fallback: string): AuthResult {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string') {
        const text = error.error.trim();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          return {
            success: false,
            message:
              'API returned a web page instead of JSON. Confirm the gateway is on https://localhost:7077 and restart both API and ng serve.',
          };
        }
        if (text) {
          return { success: false, message: text };
        }
      }

      if (error.error?.message) {
        return { success: false, message: String(error.error.message) };
      }

      if (error.error?.title) {
        return { success: false, message: String(error.error.title) };
      }

      if (error.status === 401) {
        return { success: false, message: 'Invalid email or password.' };
      }

      if (error.status === 0) {
        return {
          success: false,
          message:
            'Cannot reach the API. Open https://localhost:7077/swagger once to trust the certificate, then retry.',
        };
      }
    }

    return { success: false, message: fallback };
  }
}
